import { useState, useEffect } from 'react';
import { Pencil, DotsThree } from '@phosphor-icons/react';
import { adminFunctions } from '../../firebase/adminFunctions';
import {
  Table,
  Tbody,
  Thead,
  Tr,
  Th,
  Td,
  Button,
  Text,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Badge,
  IconButton,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select,
  useToast,
  Spinner,
} from '@chakra-ui/react';

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();

  const roleOptions = [
    { value: 'user', label: 'User' },
    { value: 'moderator', label: 'Moderator' },
    { value: 'admin', label: 'Admin' }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const usersData = await adminFunctions.getAllUsers();
      setUsers(usersData);
    } catch (error) {
      toast({
        title: 'Error loading users',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    if (!window.confirm('Are you sure you want to change this user\'s role? This will affect their permissions.')) {
      return;
    }

    setLoading(true);
    try {
      await adminFunctions.updateUserRole(userId, newRole);
      await loadUsers();
      setEditingUser(null);
      toast({
        title: 'User role updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating user role',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'red';
      case 'moderator':
        return 'blue';
      default:
        return 'gray';
    }
  };

  return (
    <Box>
      <VStack spacing={4}>
        <Input
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Box overflowX="auto" w="100%">
          {loading ? (
            <Box textAlign="center" py={8}>
              <Spinner />
            </Box>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>User</Th>
                  <Th>Email</Th>
                  <Th>Role</Th>
                  <Th>Sites</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredUsers.map((user) => (
                  <Tr key={user.id}>
                    <Td>
                      <VStack align="start" spacing={1}>
                        <Text>{user.displayName || 'Anonymous'}</Text>
                        <Text fontSize="xs" color="gray.500">ID: {user.id}</Text>
                      </VStack>
                    </Td>
                    <Td>{user.email}</Td>
                    <Td>
                      <Badge colorScheme={getRoleBadgeColor(user.role)}>
                        {user.role || 'user'}
                      </Badge>
                    </Td>
                    <Td>{user.submittedSites?.length || 0}</Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label='Options'
                          icon={<DotsThree size={20} />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem 
                            icon={<Pencil size={16} />}
                            onClick={() => setEditingUser(user)}
                          >
                            Edit Role
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>

      <Modal
        isOpen={!!editingUser}
        onClose={() => setEditingUser(null)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit User Role</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {editingUser && (
              <VStack spacing={4} align="stretch">
                <Text>User: {editingUser.displayName || 'Anonymous'}</Text>
                <Text fontSize="sm">Email: {editingUser.email}</Text>
                <Select
                  value={editingUser.role || 'user'}
                  onChange={(e) => handleUpdateUserRole(editingUser.id, e.target.value)}
                >
                  {roleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500">
                  Note: Changing a user's role will affect their access permissions across the platform.
                </Text>
                <HStack justify="flex-end">
                  <Button variant="ghost" onClick={() => setEditingUser(null)}>Cancel</Button>
                </HStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
